import { Alert, SkeletonRows } from "@vjal/ui";
import type { LiveBindingStatus } from "../hooks/useLiveProductData";

/**
 * Honest live read-model posture for product tables and KPIs.
 *
 * Renders nothing in mock/evidence mode (the contract fixtures stay
 * authoritative there) and renders exactly one of loading / denied / error /
 * empty in live data mode. Backend `CO-` support references are surfaced
 * verbatim so failures stay traceable.
 *
 * Loading uses a structural <SkeletonRows> placeholder (never a bare
 * spinner/alert) per the Product Excellence Playbook per-screen contract — the
 * `.vjal-skeleton` marker also lets the live-screen lane wait for load to clear
 * before screenshotting. SkeletonRows carries role="status"/aria-busy so the
 * loading state is announced.
 */
export function LiveListStatus({ binding, label }: { binding: LiveBindingStatus; label: string }) {
  if (!binding.isLiveBound) return null;
  if (binding.isLoading) {
    return (
      <div aria-label={`Loading live ${label}`}>
        <SkeletonRows rows={4} columns={4} />
      </div>
    );
  }
  if (binding.denied) {
    return (
      <Alert variant="info">
        {`The live service denied ${label} for this role scope${referenceSuffix(binding.reference)}. No records are shown.`}
      </Alert>
    );
  }
  if (binding.isError) {
    return <Alert variant="error">{`Live ${label} are unavailable right now${referenceSuffix(binding.reference)}.`}</Alert>;
  }
  if (binding.isEmpty) {
    return <Alert variant="info">{`No live ${label} exist yet in this organization.`}</Alert>;
  }
  return null;
}

function referenceSuffix(reference?: string) {
  return reference ? ` (ref ${reference})` : "";
}
